import React, { useState, useEffect } from "react";
import { OuroBadge, CashbackBadge, PlanPill } from "./Badges";
import { MapPin, Phone, Heart, Star, Bike, Store, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

function Stars({ value = 0, count = 0, size = 12 }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-500" data-testid="company-rating">
      {[0, 1, 2, 3, 4].map((i) => {
        const filled = i < full || (i === full && half);
        return <Star key={i} size={size} className={filled ? "fill-current" : ""}/>;
      })}
      {count > 0 && <span className="text-[10px] text-stone-500 ml-1 font-semibold">{value.toFixed(1)} ({count})</span>}
    </span>
  );
}

export default function CompanyCard({ company, onClick, active, favorites = [], onToggleFavorite }) {
  const { user } = useAuth();
  const isOuro = company.plan === "OURO";
  const isGratis = company.plan === "GRATIS";
  const showCashback = company.accepts_cashback && (company.plan === "PRATA" || company.plan === "OURO");
  const isFav = favorites.includes(company.id);

  const handleFav = async (e) => {
    e.stopPropagation();
    if (!user || user.role !== "CUSTOMER") return;
    try {
      if (isFav) await api.delete(`/favorites/${company.id}`);
      else await api.post(`/favorites/${company.id}`);
      onToggleFavorite && onToggleFavorite(company.id, !isFav);
    } catch {}
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left w-full p-4 rounded-2xl bg-white border transition-all duration-200 hover:-translate-y-[2px] hover:shadow-lg
        ${active ? "ring-2 ring-blue-800 border-blue-800 shadow-md" : "border-stone-200/70 shadow-sm"}
        ${isOuro ? "ouro-glow border-amber-400" : ""}
        ${isGratis ? "opacity-90" : ""}
      `}
      data-testid={`company-card-${company.id}`}
    >
      {company.cover_url && (
        <div className="aspect-[16/8] -m-4 mb-3 overflow-hidden rounded-t-2xl bg-stone-100">
          <img src={company.cover_url} alt={company.name} className="w-full h-full object-cover" loading="lazy"/>
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`${isGratis ? "w-9 h-9" : "w-10 h-10"} rounded-lg bg-white border border-stone-200 flex items-center justify-center overflow-hidden shrink-0`}>
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="w-full h-full object-contain p-0.5"/>
            ) : (
              <span className="font-display font-bold text-stone-400">{company.name[0]}</span>
            )}
          </div>
          <h3 className="font-display font-semibold text-stone-900 text-base leading-tight truncate">{company.name}</h3>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {user?.role === "CUSTOMER" && (
            <span onClick={handleFav} role="button" aria-label="Favoritar" data-testid={`fav-toggle-${company.id}`}
              className={`p-1 rounded-full cursor-pointer transition-colors ${isFav ? "text-rose-600" : "text-stone-300 hover:text-rose-500"}`}>
              <Heart size={16} className={isFav ? "fill-current" : ""}/>
            </span>
          )}
          <PlanPill plan={company.plan} />
        </div>
      </div>
      <p className="text-xs text-stone-500 mb-2 line-clamp-2">{company.description || company.category}</p>
      <div className="flex items-center gap-3 text-[11px] text-stone-500 mb-2 flex-wrap">
        <span className="inline-flex items-center gap-1"><MapPin size={11} />{company.address || company.category}</span>
        {company.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{company.phone}</span>}
        {company.distance_km != null && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-900 border border-blue-200 font-bold text-[10px]" data-testid={`distance-${company.id}`}>
            <MapPin size={9}/>
            {company.distance_km < 1
              ? `${Math.round(company.distance_km * 1000)} m`
              : `${company.distance_km.toFixed(company.distance_km < 10 ? 1 : 0)} km`}
          </span>
        )}
        {company.rating_count > 0 && <Stars value={company.avg_rating || 0} count={company.rating_count || 0}/>}
      </div>
      {(company.has_delivery || company.pickup_available !== false || company.business_hours) && (
        <div className="flex items-center gap-1.5 text-[10px] text-stone-600 mb-2 flex-wrap">
          {company.has_delivery && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold"><Bike size={10}/> Delivery</span>}
          {company.pickup_available !== false && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-900 border border-blue-200 font-semibold"><Store size={10}/> Retirada</span>}
          {company.business_hours && <span className="inline-flex items-center gap-1 text-stone-500 line-clamp-1"><Clock size={10}/>{company.business_hours}</span>}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {isOuro && <OuroBadge />}
        {showCashback && <CashbackBadge />}
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-stone-100 text-stone-600">{company.category}</span>
      </div>
    </button>
  );
}
